import React, { useMemo } from 'react';
import { useLayoutBuilder } from '@/shared/layoutBuilder/LayoutBuilder';
import {
  Dashboard,
  Tv,
  Campaign,
  Analytics,
  Notifications,
  Collections,
  People,
  Settings,
  Add,
  Person,
  Logout,
  Home,
  Article,
  FactCheck,
} from '@mui/icons-material';
import { useAppSelector } from '@/app';
import { useParams } from 'react-router-dom';

export interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavItem[];
}

export interface SidebarSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  path?: string;
  children: NavItem[];
  hasSubMenu: boolean; // New property to indicate if section has submenu
}

/**
 * Custom hook to get sidebar configuration from layout
 * Converts layout sidebar config to navigation items with proper hierarchy
 */
export const useSidebarConfig = () => {
  const { pageid } = useParams();
  const layout = useAppSelector(state => state.auth.user?.layout);
  const { getSidebarConfig } = useLayoutBuilder(pageid ?? 'home', layout);

  const sidebarSections = useMemo(() => {
    const sidebarConfig = getSidebarConfig();
    const sections: SidebarSection[] = [];

    sidebarConfig.forEach(section => {
      const children: NavItem[] = [];
      const hasSubMenu = Boolean(section.subMenu && section.subMenu.length > 0);

      if (section.subMenu) {
        section.subMenu.forEach(item => {
          const navItem: NavItem = {
            id: item.pageid || item.title.toLowerCase().replace(/\s+/g, '-'),
            label: item.title,
            icon: getIconComponent(item.icon),
            path: getNavPath(item.pageid, item.title),
          };
          children.push(navItem);
        });
      }

      const sidebarSection: SidebarSection = {
        id: section.title.toLowerCase().replace(/\s+/g, '-'),
        title: section.title,
        icon: getIconComponent(section.icon),
        path: getNavPath(section.pageid, section.title),
        children,
        hasSubMenu,
      };

      sections.push(sidebarSection);
    });

    return sections;
  }, [getSidebarConfig]);

  // Also provide flattened nav items for backward compatibility
  const navItems = useMemo(() => {
    const items: NavItem[] = [];
    sidebarSections.forEach(section => {
      items.push(...section.children);
    });
    return items;
  }, [sidebarSections]);

  return {
    navItems,
    sidebarSections,
    sidebarConfig: getSidebarConfig(),
  };
};

/**
 * Determines the navigation path based on pageid and title
 */
function getNavPath(pageid?: string, _title?: string): string | undefined {
  if (!pageid) return undefined;

  // Handle special cases
  if (pageid === 'signout') {
    return undefined; // Will be handled specially
  }

  // Handle external URLs
  if (pageid.startsWith('http')) {
    return pageid;
  }

  // Handle profile page (maps to our existing static route)
  if (pageid === 'profile') {
    return '/app/profile';
  }

  // Default: assume it's a dynamic page
  return `/app/${pageid}`;
}

/**
 * Maps icon string names to React icon components
 */
function getIconComponent(iconName?: string): React.ReactNode {
  switch (iconName) {
    case 'Dashboard':
      return React.createElement(Dashboard);
    case 'Campaign':
      return React.createElement(Campaign);
    case 'Analytics':
      return React.createElement(Analytics);
    case 'Notifications':
      return React.createElement(Notifications);
    case 'Collections':
      return React.createElement(Collections);
    case 'People':
      return React.createElement(People);
    case 'Settings':
      return React.createElement(Settings);
    case 'Add':
      return React.createElement(Add);
    case 'Tv':
      return React.createElement(Tv);
    case 'Person':
      return React.createElement(Person);
    case 'Logout':
      return React.createElement(Logout);
    case 'Home':
      return React.createElement(Home);
    case 'Approval':
      return React.createElement(FactCheck);
    default:
      return React.createElement(Article);
  }
}
